import React, { useMemo, useCallback, useState } from "react";

import * as yup from "yup";

import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { FormProvider, useForm } from "react-hook-form";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";
import Table from "react-bootstrap/Table";

import Input from "../../../../components/Input";
import { ToggleFeatureButton } from "../Shelly/ShellyControllers";

import {
  getClientFeatureQuery,
  QUERY_SYNGUARD,
} from "../../../../config/graphql/query";
import {
  MUTATION_CREATE_SYNGUARD,
  MUTATION_UPDATE_SYNGUARD,
  MUTATION_DELETE_SYNGUARD,
  MUTATION_DELETE_SYNGUARD_CHECKOUT_NETWORK_ITEM,
  MUTATION_CREATE_SYNGUARD_CHECKOUT_NETWORK_ITEM,
  MUTATION_START_SYNGUARD_MONITORING,
  MUTATION_STOP_SYNGUARD_MONITORING,
  TOGGLE_CLIENT_FEATURE,
} from "../../../../config/graphql/mutation";

import useHasRole from "../../../../lib/hooks/useHasRole";
import { useClientId } from "../hooks/useClient";
import { useCurrentClient } from "../../../../context/Client";

const CreateNetworkItemModal = ({
  show,
  setShow,
  synguardId,
  clientId,
}: {
  show: boolean;
  setShow: (nextValue?: any) => void;
  synguardId: string;
  clientId: string;
}) => {
  const { t } = useTranslation(["client", "common"]);

  const [networkItemId, setNetworkItemId] = useState<number>();

  const [onCreate] = useMutation(
    MUTATION_CREATE_SYNGUARD_CHECKOUT_NETWORK_ITEM,
    {
      refetchQueries: [
        {
          query: QUERY_SYNGUARD,
          context: {
            headers: {
              ClientId: clientId,
            },
          },
        },
      ],
      variables: {
        input: {
          networkItemId,
          synguard: synguardId,
        },
      },
      context: {
        headers: {
          ClientId: clientId,
        },
      },
    },
  );

  const onSubmit = () => {
    onCreate();

    return setShow(false);
  };

  return (
    <Modal
      size="sm"
      show={show}
      onHide={setShow}
      centered
      aria-labelledby="contained-modal-title-vcenter"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t("client:client.synguard.checkoutNetworkItems.modalCreate.title")}
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={onSubmit}>
        <Modal.Body>
          <label htmlFor="networkItemId">
            {t(
              "client:client.synguard.checkoutNetworkItems.modalCreate.networkItemId",
            )}
          </label>
          <input
            id="networkItemId"
            type="number"
            className="form-control"
            value={networkItemId}
            onChange={({ target: { value } }) =>
              setNetworkItemId(value === "" ? undefined : Number(value))
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShow(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" type="submit">
            {t(
              "client:client.synguard.checkoutNetworkItems.modalCreate.createBtn",
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

const TableRow = ({
  item,
  clientId,
}: {
  item: ISynguardCheckoutNetworkItem;
  clientId: string;
}) => {
  const { id, networkItemId, status } = item;

  const [visible, setVisible] = useToggle(false);

  const { t } = useTranslation(["client", "common"]);

  const [onDelete] = useMutation(
    MUTATION_DELETE_SYNGUARD_CHECKOUT_NETWORK_ITEM,
    {
      refetchQueries: [
        {
          query: QUERY_SYNGUARD,
          context: {
            headers: {
              ClientId: clientId,
            },
          },
        },
      ],
      variables: { id },
      context: {
        headers: {
          ClientId: clientId,
        },
      },
    },
  );

  const [onStartMonitoring] = useMutation(MUTATION_START_SYNGUARD_MONITORING, {
    refetchQueries: [
      {
        query: QUERY_SYNGUARD,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    variables: { id },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });
  const [onStopMonitoring] = useMutation(MUTATION_STOP_SYNGUARD_MONITORING, {
    refetchQueries: [
      {
        query: QUERY_SYNGUARD,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    variables: { id },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const onBeforeDelete = () => {
    onDelete();

    return setVisible(false);
  };

  const onBeforeStartMonitoring = async () => {
    onStartMonitoring()
      .then(() => {
        toast.success<string>(
          t(
            "client:client.synguard.checkoutNetworkItems.toast.startMonitoring",
          ),
        );
      })
      .catch(() => {
        toast.error<string>(
          t(
            "client:client.synguard.checkoutNetworkItems.toast.startMonitoringError",
          ),
        );
      });
  };

  const onBeforeStopMonitoring = async () => {
    onStopMonitoring()
      .then(() => {
        toast.success<string>(
          t("client:client.synguard.checkoutNetworkItems.toast.stopMonitoring"),
        );
      })
      .catch(() => {
        toast.error<string>(
          t(
            "client:client.synguard.checkoutNetworkItems.toast.stopMonitoringError",
          ),
        );
      });
  };

  return (
    <>
      <tr>
        <th scope="row">{networkItemId}</th>
        <td
          className={`bg-${
            status === "active" ? "success" : "warning"
          } text-white`}
        >
          {status.toUpperCase()}
        </td>
        <td className="text-nowrap">
          <div className="d-flex justify-content-around">
            <div>
              <Button
                size="sm"
                variant="success"
                className="mr-2"
                onClick={onBeforeStartMonitoring}
              >
                {t(
                  "client:client.synguard.checkoutNetworkItems.table.buttons.startMonitoring",
                )}
              </Button>
              <Button
                size="sm"
                variant="warning"
                className="mr-2 text-white"
                onClick={onBeforeStopMonitoring}
              >
                {t(
                  "client:client.synguard.checkoutNetworkItems.table.buttons.stopMonitoring",
                )}
              </Button>
            </div>
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={() => setVisible(true)}
            >
              {t("common:delete")}
            </Button>
          </div>
        </td>
      </tr>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-center">
            {t("client:client.synguard.checkoutNetworkItems.modalDelete.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.synguard.checkoutNetworkItems.modalCreate.body")}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

interface FieldValues {
  title: string;
  baseUrl: string;
  username: string;
  password: string;
  studentAccessLimit?: number;
  studentBlockedContact?: {
    name?: string;
    phone?: string;
    email?: string;
  };
}

const Synguard = React.memo(() => {
  const [show, setShow] = useToggle(false);

  const [showCreateItem, setShowCreateItem] = useToggle(false);

  const { t } = useTranslation(["client", "common"]);

  const clientId = useClientId();
  const currentClient = useCurrentClient();
  const isRocClient = currentClient?.code === "roc";

  const schema = useMemo(
    () =>
      yup.object().shape({
        title: yup.string(),
        baseUrl: yup.string().required(t("client:client.synguard.yup.baseUrl")),
        username: yup
          .string()
          .required(t("client:client.synguard.yup.username")),
        password: yup
          .string()
          .required(t("client:client.synguard.yup.password")),
      }),
    [t],
  );

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    shouldUnregister: true,
  });

  const { data: synguardData } = useQuery<{
    synguard: ISynguard;
  }>(QUERY_SYNGUARD, {
    skip: !clientId,
    onCompleted: ({ synguard }) => methods.reset(synguard),
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const synguardId = useMemo(() => synguardData?.synguard?.id, [synguardData]);

  const synguardCheckoutNetworkItems = useMemo(
    () => synguardData?.synguard?.checkoutNetworkItems ?? [],
    [synguardData],
  );

  const [onUpdate] = useMutation(MUTATION_UPDATE_SYNGUARD, {
    refetchQueries: [
      {
        query: QUERY_SYNGUARD,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const [onCreate] = useMutation(MUTATION_CREATE_SYNGUARD, {
    refetchQueries: [
      {
        query: QUERY_SYNGUARD,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const [onDelete] = useMutation(MUTATION_DELETE_SYNGUARD, {
    refetchQueries: [
      {
        query: QUERY_SYNGUARD,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const onBeforeDelete = useCallback(() => {
    setShow(false);
    return onDelete({ variables: { id: synguardId } })
      .then(() => {
        methods.reset({
          title: "",
          baseUrl: "",
          username: "",
          password: "",
        });
        toast.success<string>(t("client:client.synguard.toast.deleted"));
      })
      .catch((error) => {
        toast.error<string>(
          error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
        );
      });
  }, [onDelete, t, setShow, synguardId]);

  const onSubmit = useCallback(
    async (values: FieldValues) => {
      const processedValues = { ...values };
      if (processedValues.studentAccessLimit != null) {
        processedValues.studentAccessLimit = parseInt(
          String(processedValues.studentAccessLimit),
          10,
        );
      }

      if (synguardId) {
        return onUpdate({
          variables: { input: { id: synguardId, ...processedValues } },
        })
          .then(({ data: { updateSynguard } }) => {
            methods.reset(updateSynguard);
          })
          .then(() => {
            toast.success<string>(t("client:client.synguard.toast.updated"));
          })
          .catch((error) => {
            toast.error<string>(
              error?.networkError?.result?.errors?.[0]?.message ??
                error?.message,
            );
          });
      }

      return onCreate({ variables: { input: values } })
        .then(() => {
          toast.success<string>(t("client:client.synguard.toast.created"));
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [synguardId],
  );

  return (
    <div className="container-fluid">
      <FormProvider {...methods}>
        <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="form-group">
              <label htmlFor="title">
                {t("client:client.synguard.form.title")}
              </label>
              <Input name="title" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="baseUrl">
                {t("client:client.synguard.form.baseUrl")}
              </label>
              <Input name="baseUrl" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="username">
                {t("client:client.synguard.form.username")}
              </label>
              <Input name="username" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="password">
                {t("client:client.synguard.form.password")}
              </label>
              <Input name="password" className="form-control" />
            </div>
            <div>
              <input
                disabled={methods.formState.isSubmitting}
                type="submit"
                className="btn btn-primary mr-3"
                value={t(
                  `client:client.synguard.button.${
                    !!synguardId ? "update" : "create"
                  }`,
                )}
              />
              {!!synguardId && (
                <button
                  onClick={(e) => {
                    e.preventDefault();

                    setShow(true);
                  }}
                  className="btn btn-danger"
                >
                  {t("common:delete")}
                </button>
              )}
            </div>
          </div>

          {!!synguardId && (
            <div className="col-lg-8 col-md-6 col-sm-12">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-3">
                  {t("client:client.synguard.checkoutNetworkItems.title", {
                    count: 2,
                  })}
                </h6>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ marginTop: "-1.5rem" }}
                  onClick={(e) => {
                    e.preventDefault();

                    setShowCreateItem(true);
                  }}
                >
                  {t("client:client.synguard.checkoutNetworkItems.button.add")}
                </button>
              </div>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th scope="col">
                      {t(
                        "client:client.synguard.checkoutNetworkItems.table.th.networkItemId",
                      )}
                    </th>
                    <th scope="col">
                      {t(
                        "client:client.synguard.checkoutNetworkItems.table.th.status",
                      )}
                    </th>
                    <th scope="col">
                      {t(
                        "client:client.synguard.checkoutNetworkItems.table.th.actions",
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!!synguardCheckoutNetworkItems.length &&
                    synguardCheckoutNetworkItems.map((item) => (
                      <TableRow key={item.id} item={item} clientId={clientId} />
                    ))}
                  {!synguardCheckoutNetworkItems.length && (
                    <th colSpan={5} scope="row" className="text-center">
                      {t(
                        "client:client.synguard.checkoutNetworkItems.table.noNetworkItems",
                      )}
                    </th>
                  )}
                </tbody>
              </Table>

              {isRocClient && (
                <>
                  <hr />
                  <h6>{t("client:client.synguard.form.studentSettings")}</h6>
                  <div className="form-row">
                    <div className="form-group col-md-4">
                      <label htmlFor="studentAccessLimit">
                        {t("client:client.synguard.form.studentAccessLimit")}
                      </label>
                      <Input
                        name="studentAccessLimit"
                        type="number"
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group col-md-4">
                      <label htmlFor="studentBlockedContact.name">
                        {t("client:client.synguard.form.contactName")}
                      </label>
                      <Input
                        name="studentBlockedContact.name"
                        className="form-control"
                      />
                    </div>
                    <div className="form-group col-md-4">
                      <label htmlFor="studentBlockedContact.phone">
                        {t("client:client.synguard.form.contactPhone")}
                      </label>
                      <Input
                        name="studentBlockedContact.phone"
                        className="form-control"
                      />
                    </div>
                    <div className="form-group col-md-4">
                      <label htmlFor="studentBlockedContact.email">
                        {t("client:client.synguard.form.contactEmail")}
                      </label>
                      <Input
                        name="studentBlockedContact.email"
                        className="form-control"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </form>
      </FormProvider>
      <Modal
        size="sm"
        show={show}
        onHide={setShow}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("client:client.synguard.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.synguard.modal.body", {
            title: synguardData?.synguard?.client?.title,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShow(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
      {!!synguardId && (
        <CreateNetworkItemModal
          show={showCreateItem}
          setShow={setShowCreateItem}
          synguardId={synguardId}
          clientId={clientId}
        />
      )}
    </div>
  );
});

const SynguardFeature: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const clientId = useClientId();
  const isAdmin = useHasRole(["ADMIN"]);

  const { data } = useQuery(getClientFeatureQuery("synguard"), {
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const [toggleFeature] = useMutation(TOGGLE_CLIENT_FEATURE, {
    refetchQueries: [
      { query: getClientFeatureQuery("synguard"), variables: { id: clientId } },
    ],
  });

  const synguardEnabled = data?.client?.preferences?.synguard?.enabled ?? false;

  const onToggleSynguard = useCallback(
    async (enabled: boolean) => {
      try {
        await toggleFeature({
          variables: {
            input: {
              id: clientId,
              feature: "synguard",
              enabled,
            },
          },
        });
        toast.success<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}`, {
            feature: "Synguard",
          }),
        );
      } catch (e) {
        toast.error<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}Error`, {
            feature: "Synguard",
          }),
        );
      }
    },
    [clientId, toggleFeature, t],
  );

  if (!synguardEnabled) {
    return (
      <div className="tab-pane fade show active" id="synguard" role="tabpanel">
        {isAdmin ? (
          <ToggleFeatureButton
            featureName="Synguard"
            onClick={() => onToggleSynguard(true)}
          />
        ) : (
          <div className="text-muted ml-3">
            <h5 className="mb-3">{t("common:premiumFeature.title")}</h5>
            <p>{t("common:premiumFeature.text")}</p>
            <p>{t("common:premiumFeature.contact")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tab-pane fade show active" id="synguard" role="tabpanel">
      <Synguard />
      {isAdmin && (
        <div className="mt-3 ml-3">
          <ToggleFeatureButton
            featureName="Synguard"
            disable
            onClick={() => onToggleSynguard(false)}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(SynguardFeature);
